import { Card, Button } from '@/components/UI';
import { FaSync, FaClock } from 'react-icons/fa';
import type { PendingAnalysisResult } from './types';

interface PendingAnalysisCardProps {
    isRepairQueued: boolean;
    isStartRepairPending: boolean;
    onRetry: () => void;
    onStartRepair: () => void;
    pendingAnalysis: PendingAnalysisResult;
    retryCount: number;
}

const PendingAnalysisCard = ({
    isRepairQueued,
    isStartRepairPending,
    onRetry,
    onStartRepair,
    pendingAnalysis,
    retryCount
}: PendingAnalysisCardProps) => {
    const pipelineStatus = pendingAnalysis.pipelineStatus?.overallStatus;
    const nextRepairAction = pendingAnalysis.pipelineStatus?.nextRepairAction;
    const isPipelineActive = pipelineStatus === 'pending' || pipelineStatus === 'running';
    const isActivelyProcessing = isPipelineActive || isRepairQueued;
    const title = isActivelyProcessing ? 'Parameter discovery is running...' : 'Parameter discovery is not ready';

    return (
        <Card className="bg-zinc-900 p-6">
            <div className="text-center">
                <FaClock className={`text-4xl text-amber-400 mx-auto mb-4 ${isActivelyProcessing ? 'animate-pulse' : ''}`} />
                <p className="text-amber-400 mb-2 text-lg">{title}</p>
                <p className="text-zinc-400 mb-4">{pendingAnalysis.repairMessage}</p>
                {isActivelyProcessing ? (
                    <p className="text-zinc-500 text-sm mb-4">
                        Auto-refreshing every 5 seconds while the pipeline works on this duration/horizon pair.
                    </p>
                ) : (
                    <p className="text-zinc-500 text-sm mb-4">
                        Viewing this tab does not start or repair parameter discovery. Start it explicitly when you want to queue work.
                    </p>
                )}
                {pendingAnalysis.pipelineStatus && (
                    <p className="text-zinc-500 text-xs mb-4">
                        Pipeline status: {pendingAnalysis.pipelineStatus.overallStatus}
                        {nextRepairAction ? ` • next action ${nextRepairAction}` : ''}
                    </p>
                )}
                {retryCount > 0 && <p className="text-zinc-600 text-xs mb-4">Checking status... (attempt {retryCount})</p>}
                <div className="flex flex-wrap justify-center gap-2">
                    {!isActivelyProcessing && (
                        <Button
                            className="inline-flex items-center"
                            disabled={isStartRepairPending}
                            size="sm"
                            variant="primary"
                            onClick={onStartRepair}
                        >
                            <FaSync className={isStartRepairPending ? 'mr-2 animate-spin' : 'mr-2'} />
                            {isStartRepairPending ? 'Starting...' : 'Start/Repair Analysis'}
                        </Button>
                    )}
                    <Button className="inline-flex items-center" size="sm" variant="secondary" onClick={onRetry}>
                        <FaSync className="mr-2" />
                        Check Now
                    </Button>
                </div>
            </div>
        </Card>
    );
};

export default PendingAnalysisCard;
